import { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../contexts/AuthContext';
import './KnowYouBetter.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const DAYS_IN_MONTH = [31,29,31,30,31,30,31,31,30,31,30,31];

/* MM-DD picker — value: 'MM-DD' | '' */
const MonthDayPicker = ({ value, onChange, placeholder = '' }) => {
  const [mm, dd] = value ? value.split('-') : ['', ''];
  const monthIdx = mm ? parseInt(mm, 10) - 1 : -1;
  const maxDays = monthIdx >= 0 ? DAYS_IN_MONTH[monthIdx] : 31;

  const emit = (newMm, newDd) => {
    if (!newMm && !newDd) { onChange(''); return; }
    onChange(`${(newMm || '00').padStart(2,'0')}-${(newDd || '00').padStart(2,'0')}`);
  };

  const handleMonth = (val) => {
    const newMm = val;
    const newMaxDays = val ? DAYS_IN_MONTH[parseInt(val,10) - 1] : 31;
    const safeDd = dd && parseInt(dd,10) <= newMaxDays ? dd : '';
    emit(newMm, safeDd);
  };

  const handleDay = (val) => emit(mm, val);

  return (
    <div style={{ display:'flex', gap:'6px' }}>
      <select className="kyb-input" style={{ flex:'1' }} value={mm ? parseInt(mm,10) : ''} onChange={e => handleMonth(e.target.value)}>
        <option value="">Month</option>
        {MONTHS.map((m, i) => <option key={m} value={i+1}>{m}</option>)}
      </select>
      <select className="kyb-input" style={{ width:'80px' }} value={dd ? parseInt(dd,10) : ''} onChange={e => handleDay(e.target.value)}>
        <option value="">Day</option>
        {Array.from({ length: maxDays }, (_, i) => i+1).map(d => (
          <option key={d} value={d}>{d}</option>
        ))}
      </select>
    </div>
  );
};

const FOOD_PREFS = [
  'Vegetarian', 'Non-Vegetarian', 'Vegan', 'Jain',
  'Eggitarian', 'Halal', 'Gluten-Free', 'Dairy-Free',
];

const OCCASION_SUGGESTIONS = [
  'Work Anniversary', 'Graduation', 'Housewarming',
  'Festival', 'Pet Birthday', 'Other',
];

const blankMember = () => ({ name: '', dob: '', wedding_anniversary: '', occasions: [] });
const blankOccasion = () => ({ occasion_name: '', occasion_date: '' });

/* ── Progress indicator ─────────────────── */
const Progress = ({ step }) => (
  <div className="kyb-progress">
    {[1, 2, 3, 4].map((s, i) => (
      <>
        <div
          key={s}
          className={`kyb-step-dot${step === s ? ' active' : step > s ? ' done' : ''}`}
        >
          {step > s ? '✓' : s}
        </div>
        {i < 3 && (
          <div key={`line-${s}`} className={`kyb-step-line${step > s ? ' done' : ''}`} />
        )}
      </>
    ))}
  </div>
);

/* ── Occasion editor (reused for user + family) ── */
const OccasionEditor = ({ occasions, onChange }) => {
  const add = () => onChange([...occasions, blankOccasion()]);
  const remove = (i) => onChange(occasions.filter((_, idx) => idx !== i));
  const update = (i, field, val) =>
    onChange(occasions.map((o, idx) => idx === i ? { ...o, [field]: val } : o));

  return (
    <>
      <div className="kyb-occasion-list">
        {occasions.map((o, i) => (
          <div key={i} className="kyb-occasion-row">
            <input
              className="kyb-input"
              placeholder="Occasion name"
              list="occ-suggestions"
              value={o.occasion_name}
              onChange={e => update(i, 'occasion_name', e.target.value)}
            />
            <input
              className="kyb-input"
              type="date"
              value={o.occasion_date}
              onChange={e => update(i, 'occasion_date', e.target.value)}
            />
            <button className="kyb-remove-btn" onClick={() => remove(i)} title="Remove">×</button>
          </div>
        ))}
      </div>
      <datalist id="occ-suggestions">
        {OCCASION_SUGGESTIONS.map(s => <option key={s} value={s} />)}
      </datalist>
      <button className="kyb-add-btn" onClick={add}>+ Add Occasion</button>
    </>
  );
};

/* ── Family member card ─────────────────── */
const MemberCard = ({ member, onChange, onRemove, showAnniversary }) => (
  <div className="kyb-member-card">
    <button className="kyb-member-remove" onClick={onRemove} title="Remove">×</button>
    <div className="kyb-row">
      <div className="kyb-field">
        <label>Name (optional)</label>
        <input
          className="kyb-input"
          placeholder="Name"
          value={member.name}
          onChange={e => onChange({ ...member, name: e.target.value })}
        />
      </div>
      <div className="kyb-field">
        <label>Date of Birth</label>
        <MonthDayPicker
          value={member.dob}
          onChange={v => onChange({ ...member, dob: v })}
        />
      </div>
    </div>
    {showAnniversary && (
      <div className="kyb-field">
        <label>Wedding Anniversary</label>
        <MonthDayPicker
          value={member.wedding_anniversary}
          onChange={v => onChange({ ...member, wedding_anniversary: v })}
        />
      </div>
    )}
    <div className="kyb-field">
      <label>Special Occasions</label>
      <OccasionEditor
        occasions={member.occasions}
        onChange={occ => onChange({ ...member, occasions: occ })}
      />
    </div>
  </div>
);

/* ── Main wizard ────────────────────────── */
const KnowYouBetter = () => {
  const { user, refreshUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const isEditMode = new URLSearchParams(location.search).get('edit') === 'true';
  const auth = { headers: { Authorization: `Bearer ${user?.token}` } };

  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [error, setError] = useState('');

  /* Step 1 state */
  const [profile, setProfile] = useState({
    dob: '',
    wedding_anniversary: '',
    food_preferences: [],
    family_size: 1,
    allergies: '',
    favourite_food: '',
    favourite_meat: '',
  });

  /* Step 2 state */
  const [myOccasions, setMyOccasions] = useState([]);

  /* Step 3 state */
  const [spouse, setSpouse] = useState(null);
  const [parents, setParents] = useState([]);
  const [kids, setKids] = useState([]);

  // Load existing data on mount
  useEffect(() => {
    axios.get(`${API_URL}/user-profile/me`, auth)
      .then(({ data }) => {
        const { profile, family = [], occasions = [] } = data;
        if (profile) {
          setProfile({
            dob:                 profile.dob || '',
            wedding_anniversary: profile.wedding_anniversary || '',
            food_preferences:    profile.food_preferences || [],
            family_size:         profile.family_size || 1,
            allergies:           profile.allergies || '',
            favourite_food:      profile.favourite_food || '',
            favourite_meat:      profile.favourite_meat || '',
          });
        }
        setMyOccasions(occasions.map(o => ({
          occasion_name: o.occasion_name,
          occasion_date: o.occasion_date ? o.occasion_date.substring(0, 10) : '',
        })));
        const toMember = m => ({
          name:                m.name || '',
          dob:                 m.dob || '',
          wedding_anniversary: m.wedding_anniversary || '',
          occasions: (m.occasions || []).map(o => ({
            occasion_name: o.occasion_name,
            occasion_date: o.occasion_date ? o.occasion_date.substring(0, 10) : '',
          })),
        });
        const spouseRec = family.find(m => m.member_type === 'SPOUSE');
        setSpouse(spouseRec ? toMember(spouseRec) : null);
        setParents(family.filter(m => m.member_type === 'PARENT').map(toMember));
        setKids(family.filter(m => m.member_type === 'KID').map(toMember));
      })
      .catch(() => {})
      .finally(() => setDataLoading(false));
  }, []); // eslint-disable-line

  const toggleFoodPref = (pref) => {
    setProfile(p => ({
      ...p,
      food_preferences: p.food_preferences.includes(pref)
        ? p.food_preferences.filter(x => x !== pref)
        : [...p.food_preferences, pref],
    }));
  };

  const showMeat = profile.food_preferences.some(p =>
    ['Non-Vegetarian', 'Halal'].includes(p)
  );

  const next = () => { setError(''); setStep(s => s + 1); };
  const back = () => { setError(''); setStep(s => s - 1); };

  const skipForNow = () => {
    navigate(isEditMode ? '/account' : '/categories', { replace: true });
  };

  const neverShowAgain = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/user-profile/me`, { profile_completed: true }, auth);
      await refreshUser();
      navigate('/categories', { replace: true });
    } catch {
      setSaving(false);
    }
  };

  const saveAndNext = async () => {
    setSaving(true);
    setError('');
    try {
      /* 1. Upsert main profile */
      await axios.post(`${API_URL}/user-profile/me`, {
        ...profile,
        profile_completed: true,
      }, auth);

      /* 2. In edit mode, clear old family + occasions before re-inserting */
      if (isEditMode) {
        await axios.delete(`${API_URL}/user-profile/reset`, auth);
      }

      /* 3. User's own occasions */
      for (const occ of myOccasions) {
        if (occ.occasion_name && occ.occasion_date) {
          await axios.post(`${API_URL}/user-profile/occasions`, occ, auth);
        }
      }

      /* 4. Family members + their occasions */
      const allMembers = [
        ...(spouse ? [{ ...spouse, member_type: 'SPOUSE' }] : []),
        ...parents.map(p => ({ ...p, member_type: 'PARENT' })),
        ...kids.map(k => ({ ...k, member_type: 'KID' })),
      ];

      for (const { occasions, ...memberData } of allMembers) {
        const res = await axios.post(`${API_URL}/user-profile/family`, memberData, auth);
        const memberId = res.data.member.id;
        for (const occ of (occasions || [])) {
          if (occ.occasion_name && occ.occasion_date) {
            await axios.post(`${API_URL}/user-profile/occasions`,
              { ...occ, family_member_id: memberId }, auth);
          }
        }
      }

      await refreshUser();
      next(); // advance to step 4 success screen
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const goToDestination = () => {
    navigate(isEditMode ? '/account' : '/categories', { replace: true });
  };

  /* ── Step renders ──────────────────────── */
  const renderStep1 = () => (
    <>
      <h2 className="kyb-step-title">About You</h2>
      <p className="kyb-step-sub">Help us personalise your experience</p>

      <div className="kyb-row">
        <div className="kyb-field">
          <label>Date of Birth</label>
          <MonthDayPicker
            value={profile.dob}
            onChange={v => setProfile(p => ({ ...p, dob: v }))}
          />
        </div>
        <div className="kyb-field">
          <label>Wedding Anniversary</label>
          <MonthDayPicker
            value={profile.wedding_anniversary}
            onChange={v => setProfile(p => ({ ...p, wedding_anniversary: v }))}
          />
        </div>
      </div>

      <div className="kyb-field">
        <label>Food Preferences</label>
        <div className="kyb-chips">
          {FOOD_PREFS.map(pref => (
            <span
              key={pref}
              className={`kyb-chip${profile.food_preferences.includes(pref) ? ' selected' : ''}`}
              onClick={() => toggleFoodPref(pref)}
            >
              {pref}
            </span>
          ))}
        </div>
      </div>

      <div className="kyb-stepper">
        <span className="kyb-stepper-label">Family Size</span>
        <div className="kyb-stepper-ctrl">
          <button className="kyb-stepper-btn"
            disabled={profile.family_size <= 1}
            onClick={() => setProfile(p => ({ ...p, family_size: p.family_size - 1 }))}
          >−</button>
          <span className="kyb-stepper-val">{profile.family_size}</span>
          <button className="kyb-stepper-btn"
            disabled={profile.family_size >= 20}
            onClick={() => setProfile(p => ({ ...p, family_size: p.family_size + 1 }))}
          >+</button>
        </div>
      </div>

      <div className="kyb-field">
        <label>Any Food Allergies?</label>
        <textarea className="kyb-input kyb-textarea"
          placeholder="e.g. Peanuts, Shellfish, Lactose…"
          value={profile.allergies}
          onChange={e => setProfile(p => ({ ...p, allergies: e.target.value }))}
        />
      </div>

      <div className="kyb-row">
        <div className="kyb-field">
          <label>Favourite Food</label>
          <input className="kyb-input"
            placeholder="e.g. Biryani"
            value={profile.favourite_food}
            onChange={e => setProfile(p => ({ ...p, favourite_food: e.target.value }))}
          />
        </div>
        {showMeat && (
          <div className="kyb-field">
            <label>Favourite Meat</label>
            <input className="kyb-input"
              placeholder="e.g. Chicken"
              value={profile.favourite_meat}
              onChange={e => setProfile(p => ({ ...p, favourite_meat: e.target.value }))}
            />
          </div>
        )}
      </div>
    </>
  );

  const renderStep2 = () => (
    <>
      <h2 className="kyb-step-title">Your Special Days</h2>
      <p className="kyb-step-sub">Add occasions you'd like us to remember for you</p>
      <OccasionEditor occasions={myOccasions} onChange={setMyOccasions} />
    </>
  );

  const renderStep3 = () => (
    <>
      <h2 className="kyb-step-title">Your Family</h2>
      <p className="kyb-step-sub">Share your loved ones' details so we can celebrate with you</p>

      {/* Spouse */}
      <div className="kyb-family-section">
        <div className="kyb-family-section-head">
          <span className="kyb-family-label">💑 Spouse</span>
          {!spouse && (
            <button className="kyb-add-btn" onClick={() => setSpouse(blankMember())}>
              + Add Spouse
            </button>
          )}
        </div>
        {spouse && (
          <MemberCard
            member={spouse}
            onChange={setSpouse}
            onRemove={() => setSpouse(null)}
            showAnniversary
          />
        )}
      </div>

      <hr className="kyb-divider" />

      {/* Parents */}
      <div className="kyb-family-section">
        <div className="kyb-family-section-head">
          <span className="kyb-family-label">👨‍👩‍ Parents</span>
          <button className="kyb-add-btn"
            onClick={() => setParents(p => [...p, blankMember()])}>
            + Add Parent
          </button>
        </div>
        {parents.map((p, i) => (
          <MemberCard
            key={i}
            member={p}
            onChange={val => setParents(arr => arr.map((x, idx) => idx === i ? val : x))}
            onRemove={() => setParents(arr => arr.filter((_, idx) => idx !== i))}
            showAnniversary={false}
          />
        ))}
      </div>

      <hr className="kyb-divider" />

      {/* Kids */}
      <div className="kyb-family-section">
        <div className="kyb-family-section-head">
          <span className="kyb-family-label">👶 Kids</span>
          <button className="kyb-add-btn"
            onClick={() => setKids(k => [...k, blankMember()])}>
            + Add Child
          </button>
        </div>
        {kids.map((k, i) => (
          <MemberCard
            key={i}
            member={k}
            onChange={val => setKids(arr => arr.map((x, idx) => idx === i ? val : x))}
            onRemove={() => setKids(arr => arr.filter((_, idx) => idx !== i))}
            showAnniversary={false}
          />
        ))}
      </div>
    </>
  );

  const renderStep4 = () => (
    <div className="kyb-done">
      <div className="kyb-done-icon">🎉</div>
      <h2>You're all set!</h2>
      <p>
        Thanks for sharing — we'll use this to make your experience more personal and delightful.
        You can always update these details from your Account page.
      </p>
      <button className="kyb-done-btn" onClick={goToDestination}>
        {isEditMode ? 'Back to Account →' : 'Start Shopping →'}
      </button>
    </div>
  );

  if (dataLoading) {
    return (
      <div className="kyb-page">
        <div className="kyb-header">
          <img src="/logo.webp" alt="The New Luxury" className="kyb-logo" />
        </div>
        <div className="kyb-card" style={{ textAlign: 'center', padding: '40px' }}>
          <p style={{ color: '#9B7222' }}>Loading…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="kyb-page">
      <div className="kyb-header">
        <img src="/logo.webp" alt="The New Luxury" className="kyb-logo" />
        <h1>{isEditMode ? 'Edit Your Profile' : 'Help us know you better'}</h1>
        <p>All fields are optional — share as much or as little as you like</p>
      </div>

      <Progress step={step} />

      <div className="kyb-card">
        {error && <div className="kyb-error">{error}</div>}

        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3()}
        {step === 4 && renderStep4()}

        {step < 4 && (
          <div className="kyb-nav">
            {step > 1
              ? <button className="kyb-back-btn" onClick={back}>← Back</button>
              : <button className="kyb-skip-btn" onClick={skipForNow}>
                  {isEditMode ? 'Cancel' : 'Skip for Now'}
                </button>
            }
            <div style={{ display: 'flex', gap: '10px' }}>
              {step > 1 && (
                <button className="kyb-skip-btn" onClick={next}>Skip</button>
              )}
              <button className="kyb-next-btn" onClick={step === 3 ? saveAndNext : next} disabled={saving}>
                {step === 3 ? (saving ? 'Saving…' : 'Submit →') : 'Next →'}
              </button>
            </div>
          </div>
        )}
        {step < 4 && !isEditMode && (
          <div style={{ textAlign: 'center', marginTop: '14px' }}>
            <button
              onClick={neverShowAgain}
              disabled={saving}
              style={{ background: 'none', border: 'none', color: '#B0906A', fontSize: '15px', fontWeight: '700', cursor: 'pointer', textDecoration: 'underline' }}
            >
              Don't show this again
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default KnowYouBetter;
